/**
 * The client side of game detail odds (ISSUE-985): the odds shape this screen
 * reads from `/event/detail-get`, signed number formatting, and the one line
 * summary Matchup's `Betting line` block shows. `odds` is read defensively
 * (every new field is optional), so an older server response still renders.
 * The server decides every settlement result; this module only formats.
 */

export type typeEventOdds = {
  spread: {
    favoriteTeamId: string | null;
    points: number | null;
    isLocked: boolean;
    label?: string;
    homeOdds?: number | null;
    awayOdds?: number | null;
    result?: "home" | "away" | "push" | null;
  } | null;
  moneyline: {
    homeOdds: number | null;
    awayOdds: number | null;
    result?: "home" | "away" | "push" | null;
  } | null;
  total: {
    points: number | null;
    overOdds?: number | null;
    underOdds?: number | null;
    result?: "over" | "under" | "push" | null;
  } | null;
} | null;

export const NO_LINE_COPY = "No line yet";
export const NO_SPREAD_OR_TOTAL_COPY = "No spread or total yet";
export const ODDS_REFERENCE_NOTE = "For reference only.";
export const DEFAULT_SPREAD_LABEL = "Spread";
export const MONEYLINE_LABEL = "Moneyline";
export const TOTAL_LABEL = "Total";
export const COVERED_TAG = "Covered";
export const WON_TAG = "Won";
export const HIT_TAG = "Hit";
export const PUSH_TAG = "Push";
/** ISSUE-1086, the odds table's column headings and total prefixes. */
export const MONEY_COLUMN_LABEL = "Money";
export const TEAM_COLUMN_LABEL = "Team";
export const OVER_PREFIX = "O";
export const UNDER_PREFIX = "U";

/** "+150" for positive, "-110" for negative, "0" for zero. */
export function formatSigned(value: number): string {
  if (value > 0) return `+${String(value)}`;
  if (value === 0) return "0";
  return String(value);
}

/** The spread number from one team's side: the favorite's own points, the other team's -points; null when there is no spread. */
export function resolveTeamSpreadPoints(input: {
  favoriteTeamId: string | null;
  points: number | null;
  teamId: string;
}): number | null {
  if (input.points === null || input.favoriteTeamId === null) return null;
  return input.teamId === input.favoriteTeamId ? input.points : -input.points;
}

/** Matchup's one line. */
export function buildLineSummary(input: {
  odds: typeEventOdds;
  homeTeamId: string;
  homeText: string;
  awayTeamId: string;
  awayText: string;
}): string {
  const { odds } = input;
  const parts: string[] = [];
  const spreadPoints = odds?.spread?.points ?? null;
  const favoriteTeamId = odds?.spread?.favoriteTeamId ?? null;
  if (spreadPoints !== null && favoriteTeamId !== null) {
    const favoriteText =
      favoriteTeamId === input.homeTeamId
        ? input.homeText
        : favoriteTeamId === input.awayTeamId
          ? input.awayText
          : null;
    if (favoriteText !== null) {
      parts.push(`${favoriteText} ${formatSigned(spreadPoints)}`);
    }
  }
  const totalPoints = odds?.total?.points ?? null;
  if (totalPoints !== null) {
    parts.push(`O/U ${String(totalPoints)}`);
  }
  if (parts.length > 0) return parts.join(", ");
  if ((odds?.moneyline ?? null) !== null) return NO_SPREAD_OR_TOTAL_COPY;
  return NO_LINE_COPY;
}

export const SPREAD_PUSH_COPY = "Spread push";
export const TOTAL_PUSH_COPY = "Total push";
export const OVER_HIT_COPY = "Over hit";
export const UNDER_HIT_COPY = "Under hit";
export const COVERED_SUFFIX = "covered";

/** After a final: who covered and how the total landed, from the server's results (ISSUE-989, card Expected 1). Null when neither market has a result. */
export function buildLineResult(input: {
  odds: typeEventOdds;
  homeText: string;
  awayText: string;
}): string | null {
  const parts: string[] = [];
  const spreadResult = input.odds?.spread?.result ?? null;
  if (spreadResult === "home") {
    parts.push(`${input.homeText} ${COVERED_SUFFIX}`);
  } else if (spreadResult === "away") {
    parts.push(`${input.awayText} ${COVERED_SUFFIX}`);
  } else if (spreadResult === "push") {
    parts.push(SPREAD_PUSH_COPY);
  }
  const totalResult = input.odds?.total?.result ?? null;
  if (totalResult === "over") {
    parts.push(OVER_HIT_COPY);
  } else if (totalResult === "under") {
    parts.push(UNDER_HIT_COPY);
  } else if (totalResult === "push") {
    parts.push(TOTAL_PUSH_COPY);
  }
  return parts.length === 0 ? null : parts.join(", ");
}

/** One cell of the odds table. null in a row means "No line yet". */
export type typeOddsCell = {
  /** The cell's main figure: a spread or "O 44.5". Null for money. */
  line: string | null;
  /** The signed price, or null when the book quoted none. */
  price: string | null;
  /** Covered, Won, Hit or Push, decided by the server; null otherwise. */
  tag: string | null;
};

export type typeOddsTableRow = {
  side: "away" | "home";
  spread: typeOddsCell | null;
  total: typeOddsCell | null;
  money: typeOddsCell | null;
};

export type typeOddsTable = {
  /** "Spread", or the sport's own name for it ("Puck line", "Run line"). */
  spreadLabel: string;
  /** Away first, then home, the way a sportsbook lists a game. */
  rows: [typeOddsTableRow, typeOddsTableRow];
};

const signedOrNull = (value: number | null | undefined): string | null =>
  value === null || value === undefined ? null : formatSigned(value);

/** A cell with nothing to show reads "No line yet" rather than an empty box. */
const cellOrNull = (cell: typeOddsCell): typeOddsCell | null =>
  cell.line === null && cell.price === null && cell.tag === null ? null : cell;

/**
 * ISSUE-1086. The game's line as one sportsbook-style table: a row per team,
 * away first, with Spread, Total and Money columns. The away row carries the
 * Over and the home row the Under. Every result tag comes from the server's
 * own settlement; this only places it. A push tags both cells of its column.
 */
export function buildOddsTable(input: {
  odds: typeEventOdds;
  homeTeamId: string;
  awayTeamId: string;
}): typeOddsTable {
  const spread = input.odds?.spread ?? null;
  const total = input.odds?.total ?? null;
  const moneyline = input.odds?.moneyline ?? null;
  const teamIdBySide = { away: input.awayTeamId, home: input.homeTeamId };

  const buildRow = (side: "away" | "home"): typeOddsTableRow => {
    const spreadPoints = spread?.points ?? null;
    const favoriteTeamId = spread?.favoriteTeamId ?? null;
    const teamSpreadPoints =
      spreadPoints === null || favoriteTeamId === null
        ? null
        : resolveTeamSpreadPoints({
            favoriteTeamId,
            points: spreadPoints,
            teamId: teamIdBySide[side],
          });
    const spreadCell: typeOddsCell | null =
      spread === null || teamSpreadPoints === null
        ? null
        : {
            line: formatSigned(teamSpreadPoints),
            price: signedOrNull(
              side === "home" ? spread.homeOdds : spread.awayOdds,
            ),
            tag:
              spread.result === "push"
                ? PUSH_TAG
                : spread.result === side
                  ? COVERED_TAG
                  : null,
          };

    const totalPoints = total?.points ?? null;
    const totalSide = side === "away" ? "over" : "under";
    const totalCell: typeOddsCell | null =
      total === null || totalPoints === null
        ? null
        : {
            line: `${side === "away" ? OVER_PREFIX : UNDER_PREFIX} ${String(totalPoints)}`,
            price: signedOrNull(
              side === "away" ? total.overOdds : total.underOdds,
            ),
            tag:
              total.result === "push"
                ? PUSH_TAG
                : total.result === totalSide
                  ? HIT_TAG
                  : null,
          };

    const moneyCell: typeOddsCell | null =
      moneyline === null
        ? null
        : cellOrNull({
            line: null,
            price: signedOrNull(
              side === "home" ? moneyline.homeOdds : moneyline.awayOdds,
            ),
            tag:
              moneyline.result === "push"
                ? PUSH_TAG
                : moneyline.result === side
                  ? WON_TAG
                  : null,
          });

    return { side, spread: spreadCell, total: totalCell, money: moneyCell };
  };

  return {
    spreadLabel: spread?.label ?? DEFAULT_SPREAD_LABEL,
    rows: [buildRow("away"), buildRow("home")],
  };
}
